/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useState, useEffect, Fragment } from 'react';
import Style from './../css/PaymentsAndDisbursements.module.css';
import { Button, TextField, Select, MenuItem, Autocomplete } from '@mui/material';
import EnvelopeWithBox from '../components/EnvelopeWithBox';
import LoadingModal from '../components/modals/LoadingModal';
import type { QidType } from '../types/QidType';
import useGetPaymentSuffixQidList from '../common/hooks/useGetPaymentSuffixQidList';
import useGetPaymentTransactionQidList from '../common/hooks/useGetPaymentTransactionQidList';
import { SnackbarProvider } from 'notistack';
import { useAuthContext } from '../contexts/AuthContext';
import { useViewingContext } from '../contexts/ViewingContext';
import LoanPaymentTable from './LoanPaymentTable';
import {
  TAX_DISBURSEMENT_CODES,
  HAZARD_INSURANCE_CODES,
  MORTGAGE_INSURANCE_PREMIUM_CODES,
  HOA_DISBURSEMENT_CODES,
  GROUND_RENT_DISBURSEMENT_CODES,
  UNUSED_CODES,
  UNUSED_SUFFIXES,
} from '../contracts/constants';
import useGetBatchList from '../common/hooks/useGetBatchesList';
import useGetBatchServPayments from '../common/hooks/useGetBatchServPayments';

function PaymentsAndDisbursmentsPage() {
  const [loading, setLoading] = useState(false);
  const [batchNumber, setBatchNumber] = useState('');
  const [tempBatchNumber, setTempBatchNumber] = useState('');
  const { suffixQidList, loadingSuffixQid } = useGetPaymentSuffixQidList();
  const { transactionQidList, loadingTransactionQid } = useGetPaymentTransactionQidList();
  const [transactionCode, setTransactionCode] = useState('');
  const { token, fullAccess } = useAuthContext();
  const { isServicerView } = useViewingContext();
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [filteredSuffixList, setFilteredSuffixList] = useState([]);
  const { filteredBatches, loadingBatches } = useGetBatchList();
  const { payments, loadingBatch, batchErrorMessages } = useGetBatchServPayments(batchNumber);
  const [activeBatches, setActiveBatches] = useState([]);

  // Judith Espinal - 04/01/2026 -SQC-590: Use filteredBatches from hook (pre-filtered by postedSource, keyword, and amountPosted)
  useEffect(() => {
    if (filteredBatches) {
      setActiveBatches(filteredBatches.map((b) => b.batchNo));
    }
  }, [filteredBatches]);

  const setBatchValue = (_, value: string) => {
    //this method helps us prevent against nulls in the batch number chooser
    if (value) {
      setTempBatchNumber(value);
    } else {
      setTempBatchNumber('');
    }
  };

  const resetBatch = () => {
    setBatchNumber('');
    setTempBatchNumber('');
    setTransactionCode('');
  };

  useEffect(() => {
    let temp = transactionQidList.filter((transaction) => !UNUSED_CODES.includes(transaction.code));
    temp = temp.filter((transaction) => !TAX_DISBURSEMENT_CODES.includes(transaction.code));
    temp = temp.filter((transaction) => !HAZARD_INSURANCE_CODES.includes(transaction.code));
    temp = temp.filter((transaction) => !MORTGAGE_INSURANCE_PREMIUM_CODES.includes(transaction.code));
    temp = temp.filter((transaction) => !HOA_DISBURSEMENT_CODES.includes(transaction.code));
    temp = temp.filter((transaction) => !GROUND_RENT_DISBURSEMENT_CODES.includes(transaction.code));
    temp.push({ code: 'TX', description: 'Tax Disbursement' });
    temp.push({ code: 'HA', description: 'HOA Disbursement' });
    temp.push({ code: 'GR', description: 'Ground Rent Disbursement' });
    temp.push({ code: 'HZ', description: 'Hazard Insurance Disbursement' });
    temp.push({ code: 'MI', description: 'Mortgage Insurance Disbursement' });
    // change description for transaction code 14
    temp = temp.map((item) => (item.code === '14' ? { ...item, description: 'Pay Late Fee' } : item));
    setFilteredTransactions(temp);
  }, [transactionQidList]);

  useEffect(() => {
    setFilteredSuffixList(suffixQidList.filter((suffix) => !UNUSED_SUFFIXES.includes(suffix.code)));
  }, [suffixQidList]);

  useEffect(() => {
    setLoading(loadingSuffixQid || loadingTransactionQid || loadingBatches);
  }, [loadingSuffixQid, loadingTransactionQid, loadingBatches]);

  // Judith Espinal - 04/20/2026 - SQC-557 Issue 1: true only after user clicks Retrieve Batch and the batch-payments request is in flight (not the initial suffix/TR/batch-list load).
  const retrieveBatchLoading = Boolean(batchNumber) && loadingBatch;

  return (
    <Fragment>
      <EnvelopeWithBox blockName='Payments And Disbursements' className={Style.paymentsEnvelope}>
        {loading ? (
          <LoadingModal open={loading} />
        ) : (
          <div className={Style.pageContent}>
            {/* overlay while batch transactions load so rows do not flash then disappear. */}
            {retrieveBatchLoading && <LoadingModal open />}
            <div className={Style.batchToolbar}>
              <Autocomplete
                options={activeBatches}
                value={tempBatchNumber}
                onChange={setBatchValue}
                className={Style.textInput}
                disabled={Boolean(batchNumber)}
                renderInput={(params) => <TextField {...params} />}
              />
              <Select
                value={transactionCode}
                onChange={(e) => setTransactionCode(e.target.value)}
                className={Style.dropdown}
                displayEmpty
                disabled={Boolean(batchNumber) || !fullAccess}
              >
                <MenuItem key={'blank'} value={''}>
                  {'Select Transaction Type'}
                </MenuItem>
                <MenuItem key={'mult'} value={'MP'}>
                  {'Multiple Payments'}
                </MenuItem>
                {filteredTransactions.map((row: QidType, index: number) => (
                  <MenuItem key={index} value={row.code}>
                    {row.code} - {row.description}
                  </MenuItem>
                ))}
              </Select>
              <Button
                variant='contained'
                className={Style.seperation}
                onClick={() => setBatchNumber(tempBatchNumber)}
                disabled={!tempBatchNumber || !transactionCode}
              >
                Retrieve Batch
              </Button>
              <Button
                disabled={!fullAccess && isServicerView}
                variant='contained'
                color='error'
                className={Style.seperation}
                onClick={() => resetBatch()}
              >
                Reset
              </Button>
            </div>
            {transactionCode !== '' && (
              <div className={Style.tableHeight}>
                {/* payments/loadingBatch/batchErrorMessages come from useGetBatchServPayments on this page. */}
                <LoanPaymentTable
                  transactionCode={transactionCode}
                  suffixQidList={filteredSuffixList}
                  transactionQidList={transactionQidList}
                  batchNumber={batchNumber}
                  token={token}
                  payments={payments}
                  loadingBatch={loadingBatch}
                  batchErrorMessages={batchErrorMessages}
                />
              </div>
            )}
          </div>
        )}
      </EnvelopeWithBox>
    </Fragment>
  );
}

export default function PaymentAndDisbursements() {
  return (
    <SnackbarProvider maxSnack={3} preventDuplicate anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <PaymentsAndDisbursmentsPage />
    </SnackbarProvider>
  );
}
